import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

export const ImageWithLoader = ({ 
  src, 
  alt, 
  className = '',
  aspectRatio = '16/9',
  objectFit = 'cover',
  fallback = null,
  blur = true,
  ...props 
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => setLoaded(true);
    img.onerror = () => setError(true);
  }, [src]);

  if (error && fallback) {
    return fallback;
  }

  return (
    <div 
      className={clsx('relative overflow-hidden bg-gray-200 dark:bg-gray-700', className)}
      style={{ aspectRatio }}
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600" />
        </div>
      )}
      
      <motion.img
        src={src}
        alt={alt}
        className={clsx(
          'w-full h-full',
          objectFit === 'cover' && 'object-cover',
          objectFit === 'contain' && 'object-contain',
          !loaded && 'opacity-0'
        )}
        initial={{ opacity: 0, scale: blur ? 1.1 : 1 }}
        animate={loaded ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.5 }}
        {...props}
      />
    </div>
  );
};

export const ImageGallery = ({ images = [], columns = 3, gap = 4 }) => {
  return (
    <div 
      className={clsx('grid gap-' + gap)}
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
      }}
    >
      {images.map((image, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
          whileHover={{ scale: 1.05 }}
          className="cursor-pointer"
        >
          <ImageWithLoader
            src={image.src}
            alt={image.alt || `Gallery image ${index + 1}`}
            className="rounded-xl shadow-lg"
          />
        </motion.div>
      ))}
    </div>
  );
};

export const ParallaxImage = ({ 
  src, 
  alt, 
  className = '',
  speed = 0.5 
}) => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.pageYOffset * speed);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return (
    <div className={clsx('relative overflow-hidden', className)}>
      <motion.img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        style={{ y: offset }}
      />
    </div>
  );
};

export const OptimizedImage = ({ 
  src, 
  alt,
  width,
  height,
  lazy = true,
  className = '',
  ...props 
}) => {
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={lazy ? 'lazy' : 'eager'}
      className={className}
      {...props}
    />
  );
};

export const BackgroundImage = ({ 
  src, 
  overlay = true,
  overlayColor = 'black',
  overlayOpacity = 0.5,
  children,
  className = '' 
}) => {
  return (
    <div className={clsx('relative', className)}>
      <div className="absolute inset-0 z-0">
        <img 
          src={src} 
          alt="" 
          className="w-full h-full object-cover"
        />
        {overlay && (
          <div 
            className="absolute inset-0"
            style={{ 
              backgroundColor: overlayColor, 
              opacity: overlayOpacity 
            }}
          />
        )}
      </div>
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export const ImageCarousel = ({ images = [], autoplay = true, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!autoplay) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoplay, interval, images.length]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {images.map((image, index) => (
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: index === currentIndex ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          <ImageWithLoader
            src={image.src}
            alt={image.alt || `Slide ${index + 1}`}
            className="w-full h-full"
          />
        </motion.div>
      ))}
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={clsx(
              'w-2 h-2 rounded-full transition-all',
              index === currentIndex 
                ? 'bg-white w-8' 
                : 'bg-white/50 hover:bg-white/75'
            )}
          />
        ))}
      </div>
    </div>
  );
};
